import moment from 'moment'
import * as R from 'ramda'
import * as Offering from '../offering/types'
import { mergeObjects } from '../../../common/ramda-utils'
const { QuickTender, QuickTenderScopePending, noRatings, fromDomain, toDomain } = Offering
import { makeFlexibleOffering } from '../offering/recommendation'
import { upgradeToQuickTenderWithRatings } from '../offering/quick-tender'
import {
  Offer,
  AssessmentQuestion,
  AssessmentQuestionKey,
  AssessmentEquipmentGroupAnswers,
  ClientFrontlineConfig,
  Equipment,
  CommercialTerms,
  Opportunity
} from '../../../../types/types'
import * as AssessmentResult from '../assessmentResults/dto'
import {
  fillPricesForExtraServices,
  extractServices,
  fillPricesForExtraCommercialTerms,
  extractCommercialTerms
} from '../pricing/extra-services'
import { collectGroups } from '../assessmentResults/equipment'
import { EquipmentDate } from 'nemo-pricing'
import { isMultiSelectCommercialTerm, resetCommercialTerms } from '../../utils/commercialterms/CommercialTermsUtils'
import {
  isAUGRUCommercialTermsConfiguredInMBS,
  isBillingPlanCommercialTermsConfiguredInMBS
} from '../frontline-config/utils'
import { getEquipmentCountChanges } from '../escalation/escalation-offer-utils'
import { setInitialData, setInitialWarrantyOptions } from '../offer'
import { getUUID } from 'app/app/common/common-utils'

export const updateReasonForCriticalityAnswers = <T extends AssessmentEquipmentGroupAnswers>(
  answersByGroup: T,
  offer: Offer
): T => {
  const allEquipment = R.mergeAll(
    R.map(building => R.indexBy(R.prop('salesToolId'), building.details.equipment), offer.buildings)
  )

  // Reason for criticality can only exist when the equipment has been set as critical
  return R.mapObjIndexed((answers, equipmentId) => {
    const {
      CRITICALITY_FOR_CUSTOMERS_REASON,
      CRITICALITY_FOR_CUSTOMERS_REASON_ESCALATOR,
      CRITICALITY_FOR_CUSTOMERS_REASON_DOOR,
      ...otherAnswers
    } = answers as any
    return {
      ...otherAnswers,
      ...(allEquipment[equipmentId] && allEquipment[equipmentId].criticality === 'critical'
        ? {
            CRITICALITY_FOR_CUSTOMERS_REASON,
            CRITICALITY_FOR_CUSTOMERS_REASON_ESCALATOR,
            CRITICALITY_FOR_CUSTOMERS_REASON_DOOR
          }
        : undefined)
    }
  }, answersByGroup) as T
}

export const updateAssessmentAndEquipmentAnswers = (
  questions: AssessmentQuestion[],
  answersByGroup: AssessmentEquipmentGroupAnswers,
  offer: Offer
) => {
  const answersFilteredByCriticality = updateReasonForCriticalityAnswers(answersByGroup, offer)

  // Go through questions and remove an answer if the question or choice has been removed
  const keys = questions.map(R.prop('key'))
  const questionsChoices = questions.map(q => ({ ...q, choices: q.choices.map(({ key }) => key) }))
  const questionsKeyed = R.groupBy(R.prop('key'), questionsChoices)
  // @ts-ignore
  return R.map(
    R.pipe(R.pick<PRecord<AssessmentQuestionKey, number>, AssessmentQuestionKey>(keys), answers =>
      R.pickBy((v, k) => R.find(c => c === v, questionsKeyed[k][0].choices) !== undefined, answers)
    ),
    answersFilteredByCriticality
  )
}

// NOTE: default values are supported for types 'select' and 'text'
export const updateCommercialTerms = (
  frontlineConfig: ClientFrontlineConfig,
  commercialTerms: CommercialTerms,
  opportunity: Opportunity,
  isContractStartDateAtEquipmentLevel: boolean,
  isBillingPlanAtEquipmentLevel: undefined | boolean,
  isOrderReasonAtEquipmentLevel: undefined | boolean
) => {
  const updatedCommercialTerms: CommercialTerms = R.reduce(
    (acc, { key, type, values, pair, default_value, is_multiselect, multiselect_default_values }) => {
      if (R.has(key, commercialTerms)) {
        const value: string | string[] | undefined | EquipmentDate = commercialTerms[key as keyof CommercialTerms]
        switch (type) {
          case 'select':
            if (!Array.isArray(values)) {
              return acc
            }
            const isValueValid = (val: string | undefined): boolean => R.indexOf(val, values as any) !== -1
            if (isMultiSelectCommercialTerm(key)) {
              if (typeof value === 'string' && isValueValid(value as string)) {
                return R.assoc(key, [value], acc)
              }
              if (Array.isArray(value) && value.length) {
                const newValue: string[] = value.filter((val: string) => isValueValid(val))
                if (newValue.length) {
                  return R.assoc(key, newValue, acc)
                }
              }
              if (!R.isNil(default_value) || !R.isNil(multiselect_default_values)) {
                return is_multiselect && !R.isNil(multiselect_default_values)
                  ? R.assoc(key, multiselect_default_values, acc)
                  : R.assoc(key, [default_value], acc)
              } else {
                return R.assoc(key, [], acc)
              }
            }
            if (key === 'KCSM_BILLING_PLAN' && isBillingPlanAtEquipmentLevel) {
              if (
                !frontlineConfig.enable_billing_plan_at_equipment_level.active ||
                !isBillingPlanCommercialTermsConfiguredInMBS(frontlineConfig.commercialTerms)
              ) {
                return R.assoc(key, null, acc)
              }
              return R.assoc(key, value, acc)
            }
            if (key === 'AUGRU' && isOrderReasonAtEquipmentLevel) {
              if (
                !frontlineConfig.enable_order_reason_at_equipment_level.active ||
                !isAUGRUCommercialTermsConfiguredInMBS(frontlineConfig.commercialTerms)
              ) {
                return R.assoc(key, null, acc)
              }
              return R.assoc(key, value, acc)
            }
            if (isValueValid(value as string)) {
              return R.assoc(key, value, acc)
            }
            if (!R.isNil(default_value)) {
              return R.assoc(key, default_value, acc)
            }
            return acc
          case 'pair-select':
            const pairValue = pair ? commercialTerms[pair as keyof CommercialTerms] : undefined
            const validValue = (values as any).find((x: any) => x.key === value && pairValue && x.value === pairValue)
            return validValue ? R.pipe(R.assoc(key, value), R.assoc(pair!, pairValue))(acc) : acc
          case 'date':
          case 'flexible-date':
            if (
              isContractStartDateAtEquipmentLevel &&
              !frontlineConfig.contactDateAtEquipmentLevel.active &&
              ['VBEGDAT', 'KCSM_BASE_MONTH_ESCALATION', 'VENDDAT', 'VABNDAT'].includes(key)
            ) {
              return R.assoc(key, null, acc)
            }
            return R.assoc(key, value, acc)
          default:
            return R.assoc(key, value, acc)
        }
      }
      if (is_multiselect && !R.isNil(multiselect_default_values)) {
        return R.assoc(key, multiselect_default_values, acc)
      }
      return R.isNil(default_value) ? acc : R.assoc(key, default_value, acc)
    },
    {},
    frontlineConfig.commercialTerms
  )

  return resetCommercialTerms(frontlineConfig, updatedCommercialTerms, opportunity, true)
}

export const updateIntroductionTexts = (
  frontlineConfig: ClientFrontlineConfig,
  introductionTexts: Record<string, string>
) => R.mergeDeepRight(frontlineConfig.introductionTexts, introductionTexts)

const updateExtraServices =
  (frontlineConfig: ClientFrontlineConfig) =>
  (offer: Offer): Offer => {
    const offering = toDomain(offer)
    const assessment = AssessmentResult.toDomain(offer, frontlineConfig)
    if (
      !(
        (assessment._tag === 'ValidForFlexible' || assessment._tag === 'ValidForFlexibleDoors') &&
        offering._tag === 'Flexible'
      )
    ) {
      return offer
    }
    const groups = collectGroups(assessment)
    return {
      ...offer,
      extraServices:
        offer.extraServices && offer.extraServices.length > 0
          ? fillPricesForExtraServices(extractServices(offering, groups, frontlineConfig), offer.extraServices)
          : extractServices(offering, groups, frontlineConfig),
      extraCommercialTerms:
        offer.extraCommercialTerms && offer.extraCommercialTerms.length > 0
          ? fillPricesForExtraCommercialTerms(
              extractCommercialTerms(offering, offer.commercialTerms, groups),
              offer.extraCommercialTerms
            )
          : extractCommercialTerms(offering, offer.commercialTerms, groups)
    }
  }

const updateOffering = (frontlineConfig: ClientFrontlineConfig) => (offer: Offer) => {
  const { offeringQuestions, enableDXOffering, enableCfalOffering } = frontlineConfig
  const assessment = AssessmentResult.toDomain(offer, frontlineConfig)
  const offering = toDomain(offer)
  switch (assessment._tag) {
    case 'ValidForQuickTender':
      return offering instanceof QuickTender
        ? fromDomain(offer, Offering.repairOfferingSelections(offeringQuestions, offering, offer))
        : fromDomain(offer, new QuickTenderScopePending({}))
    case 'ValidForValueAddedServiceAddendum':
      // Assume that the offer has always been and will be a VAS addendum
      // if it has once been of that type.
      return fromDomain(offer, Offering.repairOfferingSelections(offeringQuestions, offering, offer))
    case 'ValidForGSMCampaign': // TODO: Remove as part of GSM Clean up
      // Assume that the offer has always been and will be a VAS addendum
      // if it has once been of that type.
      return fromDomain(offer, Offering.repairOfferingSelections(offeringQuestions, offering, offer))
    case 'ValidForFlexibleDoors':
      return fromDomain(offer, Offering.repairOfferingSelections(offeringQuestions, offering, offer))
    case 'ValidForFlexible':
      switch (offering._tag) {
        case 'QuickTender':
          // Continue with quicktender but fix selections
          const upgraded = upgradeToQuickTenderWithRatings(offering, assessment)
          const repaired = Offering.repairOfferingSelections(offeringQuestions, upgraded, offer)
          return fromDomain(offer, repaired)
        case 'QuickTenderWithRatings':
          // Continue with quick tender but fix selections
          return fromDomain(offer, Offering.repairOfferingSelections(offeringQuestions, offering, offer))
        case 'Flexible':
          return fromDomain(offer, Offering.repairOfferingSelections(offeringQuestions, offering, offer))
        case 'QuickTenderScopePending':
        case 'NoRatings':
        default:
          return fromDomain(
            offer,
            makeFlexibleOffering(
              offeringQuestions.group,
              assessment,
              offer,
              enableDXOffering.active,
              enableCfalOffering.active
            )
          )
      }
    case 'Invalid':
    default:
      return fromDomain(offer, noRatings)
  }
}

const getTechnicalId = (frontlineConfig: ClientFrontlineConfig, equipment: Equipment) =>
  equipment.equipmentCategory === 'elevator'
    ? frontlineConfig.elevatorQuestions.filter(obj => obj.key === 'technicalId').length === 0
      ? null
      : equipment.technicalId
    : equipment.technicalId
const updateEquipmentMasterData =
  (frontlineConfig: ClientFrontlineConfig) =>
  (offer: Offer): Offer => ({
    ...offer,
    buildings: offer.buildings.map(building => ({
      ...building,
      details: {
        ...building.details,
        equipment: building.details.equipment.map(e => ({
          ...e,
          technicalId: getTechnicalId(frontlineConfig, e),
          locationAddress: e.locationAddress || building.details.buildingAddress,
          locationCity: e.locationCity || building.details.buildingCity,
          locationCountry: e.locationCountry || building.details.locationCountry,
          locationPostalCode: e.locationPostalCode || building.details.locationPostalCode,
          locationRegion: e.locationRegion || building.details.locationRegion,
          equipmentAddress: e.equipmentAddress || building.details.buildingAddress
        }))
      }
    }))
  })

const updateContractAttachments =
  (frontlineConfig: ClientFrontlineConfig) =>
  (offer: Offer): Offer => {
    const attachments = Array.isArray(offer.attachments)
      ? R.intersection(offer.attachments, frontlineConfig.attachmentOptions)
      : undefined
    const customAttachmentSelection = Array.isArray(offer.customAttachmentSelection)
      ? R.intersection(offer.customAttachmentSelection, frontlineConfig.customAttachment)
      : []

    return {
      ...offer,
      attachments,
      customAttachmentSelection,
      customAttachmentComments: !R.isEmpty(customAttachmentSelection) ? offer.customAttachmentComments : ''
    }
  }

export const updateOfferConfiguration = (frontlineConfig: ClientFrontlineConfig, offer: Offer): Offer => {
  const { assessmentQuestions, enableVasSalesDiscount } = frontlineConfig

  const isCFALEnabled = frontlineConfig.enableCfalOffering.active
  const { equipmentsInOpportunity, equipmentsInOffer, isEquipmentEnabledInOffer, contractEquipmentsTotalAnnualValue } =
    getEquipmentCountChanges(offer.opportunity, offer.buildings, offer, frontlineConfig)

  const updatedOffer = {
    ...offer,
    isVasDiscountEnabledInFL: enableVasSalesDiscount.active,
    equipmentsInOpportunity,
    equipmentsInOffer,
    isEquipmentEnabledInOffer,
    contractEquipmentsTotalAnnualValue,
    isCFALEnabled
  }

  return R.pipe(
    (o: Offer) =>
      ({
        ...o,
        assessmentEquipmentGroupAnswers: updateAssessmentAndEquipmentAnswers(
          offer.assessmentMethod === 'standard' ? assessmentQuestions.building.all : assessmentQuestions.group.all,
          offer.assessmentEquipmentGroupAnswers,
          o
        )
      }) as Offer,
    updateEquipmentMasterData(frontlineConfig),
    updateOffering(frontlineConfig),
    o =>
      ({
        ...o,
        commercialTerms: updateCommercialTerms(
          frontlineConfig,
          offer.commercialTerms || {},
          offer.opportunity,
          offer.isContractStartDateAtEquipmentLevel,
          offer.isBillingPlanAtEquipmentLevel,
          offer.isOrderReasonAtEquipmentLevel
        ),
        introductions: updateIntroductionTexts(frontlineConfig, offer.introductions),
        isContractStartDateAtEquipmentLevel: frontlineConfig.contactDateAtEquipmentLevel.active
          ? offer.isContractStartDateAtEquipmentLevel
          : false,
        isBillingPlanAtEquipmentLevel:
          frontlineConfig.enable_billing_plan_at_equipment_level.active &&
          isBillingPlanCommercialTermsConfiguredInMBS(frontlineConfig.commercialTerms)
            ? offer.isBillingPlanAtEquipmentLevel
            : false,
        isOrderReasonAtEquipmentLevel:
          frontlineConfig.enable_order_reason_at_equipment_level.active &&
          isAUGRUCommercialTermsConfiguredInMBS(frontlineConfig.commercialTerms)
            ? offer.isOrderReasonAtEquipmentLevel
            : false
      }) as Offer,
    updateExtraServices(frontlineConfig),
    setInitialWarrantyOptions(frontlineConfig) as (o: Offer) => Offer,
    updateContractAttachments(frontlineConfig)
  )(updatedOffer)
}
function resetEquipmentProductOfferingIds(offer: Offer) {
  if (!offer || !Array.isArray(offer.buildings)) {
    return offer
  }

  const updatedBuildings = offer.buildings.map(building => {
    if (!building || !building.details || !Array.isArray(building.details.equipment)) {
      return building
    }

    const updatedEquipment = building.details.equipment.map(equipment => ({
      ...equipment,
      productOfferingId: null
    }))

    return {
      ...building,
      details: {
        ...building.details,
        equipment: updatedEquipment
      }
    }
  })

  return {
    ...offer,
    buildings: updatedBuildings
  }
}

export const createNextOfferVersion = (offer: Offer, frontlineConfig: ClientFrontlineConfig) => {
  const nextOffer = R.pipe(
    (obj: any) => mergeObjects(offer, obj),
    resetEquipmentProductOfferingIds
  )({
    prices: [],
    summaryAttachmentId: null,
    tenderVersionId: null,
    tenderVersionName: null,
    tenderVersionNumber: null,
    tenderVersionComment: '',
    completed: false,
    contractEmailTimestamp: undefined,
    id: getUUID(),
    enablerFilename: undefined,
    contractFilenames: { docgen: {}, gendoc: {} },
    sapContractTimestamp: undefined,
    sapContractFilenames: [],
    salesforceTenderTimestamp: null,
    finalized: false,
    sfTenderVersionActive: false,
    discountApprovalStatus: 'pending',
    discountApprovalComment: null,
    discountSummary: false,
    finalization_started: null,
    vasSetupFee: undefined,
    priceEstimation: undefined
  } as Partial<Offer>)

  // Preserve the equipmentGroups before updating configuration
  const previousEquipmentGroups = offer.equipmentGroups

  const updatedOffer = updateOfferConfiguration(frontlineConfig, nextOffer)

  // Always preserve equipmentGroups with selections from previous version
  const offerWithEquipmentGroups = previousEquipmentGroups
    ? { ...updatedOffer, equipmentGroups: previousEquipmentGroups }
    : updatedOffer

  return setInitialData(true, frontlineConfig)(offerWithEquipmentGroups)
}

const reset = (frontlineConfig: ClientFrontlineConfig) => (offer: Offer) => {
  const { completed } = offer

  if (!completed) {
    return {} as Offer
  }
  const f = createNextOfferVersion(offer, frontlineConfig)
  return f
}

export const setOfferModified = (offer: Offer) => {
  // const c = R.mergeDeepRight(offer, {
  //   modified_client: moment.utc().format(),
  //   completed: false,
  //   finalizationProgress: {}
  // })
  const c = { ...offer, modified_client: moment.utc().format(), completed: false, finalizationProgress: {} }
  return c
}

export const getUpdatedOfferFields = (offer: Offer, frontlineConfig: ClientFrontlineConfig) => {
  const g = R.pipe(reset(frontlineConfig), setOfferModified)(offer)
  return g
}
